import { Frown, Meh, Smile } from "lucide-react";
import { Badge, type Tone } from "@/components/ui/badge";
import { scoreLevel } from "@/lib/format";
import { useT } from "@/lib/i18n";
import type { Intent, Sentiment } from "@/lib/types";

/** Shared label chips for AI intent and sentiment (inbox, approvals, automation runs). */
export const INTENT_TONE: Record<Intent, Tone> = {
  question: "info",
  price: "accent",
  purchase: "ok",
  collab: "violet",
  complaint: "bad",
  praise: "ok",
  spam: "neutral",
  hate: "bad",
  other: "neutral",
};

export const INTENT_EMOJI: Record<Intent, string> = {
  question: "❓",
  price: "💰",
  purchase: "🛍️",
  collab: "🤝",
  complaint: "⚠️",
  praise: "💙",
  spam: "🚫",
  hate: "⛔",
  other: "💬",
};

export function IntentChip({ intent, confidence, size = "sm" }: { intent: Intent | null | undefined; confidence?: number | null; size?: "xs" | "sm" | "md" }) {
  const { t } = useT();
  if (!intent) return null;
  return (
    <Badge tone={INTENT_TONE[intent]} size={size} title={confidence ? `${t("common.ai.confidence")}: ${t(`common.level.${scoreLevel(confidence)}`)}` : undefined}>
      {t(`common.intents.${intent}`)}
    </Badge>
  );
}

export function SentimentChip({ sentiment, size = "sm", iconOnly }: { sentiment: Sentiment | null | undefined; size?: "xs" | "sm" | "md"; iconOnly?: boolean }) {
  const { t } = useT();
  if (!sentiment) return null;
  const Icon = sentiment === "positive" ? Smile : sentiment === "negative" ? Frown : Meh;
  const tone: Tone = sentiment === "positive" ? "ok" : sentiment === "negative" ? "bad" : "neutral";
  if (iconOnly)
    return (
      <span role="img" title={t(`common.sentiments.${sentiment}`)} aria-label={t(`common.sentiments.${sentiment}`)} className={sentiment === "positive" ? "text-ok" : sentiment === "negative" ? "text-bad" : "text-ink-3"}>
        <Icon className="size-4" />
      </span>
    );
  return (
    <Badge tone={tone} size={size} icon={<Icon />}>
      {t(`common.sentiments.${sentiment}`)}
    </Badge>
  );
}
